import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import toast from 'react-hot-toast';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import RetryIcon from 'assets/rotate.svg?react';
import RetryOrangeIcon from 'assets/rotate_orange_icon.svg?react';
import { Formik } from 'formik';
import styled from 'styled-components';
import * as Yup from 'yup';

import { StepTitle } from '../../../pages/ScenariosGeneratorPage/ScenariosGeneratorPage';
import { useGenerateIdeaMutation } from '../../../services/api/generateScenarioApi';
import { Spinner } from '../../ira/ui/Spinner';

export const ScriptIdeaTool = (props) => {
  const {
    initialScenarioIdea = '',
    initialPlayerName = '',
    initialTags = [],
    allTags = [],
    onContinue = () => { },
    onChange = () => { },
  } = props;

  const [name, setName] = useState(initialPlayerName || '');
  const [scriptIdea, setScriptIdea] = useState(initialScenarioIdea);
  const [selectedTags, setSelectedTags] = useState(initialTags);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [query, setQuery] = useState('');

  const [generateIdea, { isSuccess }] = useGenerateIdeaMutation();


  const handleGenerateClick = async () => {
    if (generating) {
      return;
    }
    if (name?.trim() === '') {
      toast.error('Enter name');
      return;
    }
    if (selectedTags?.length === 0) {
      toast.error('Select at least one tag');
      return;
    }

    setGenerating(true);
    try {
      const response = await generateIdea({ keywords: selectedTags, playerName: name });

      if (response?.data) {
        setScriptIdea(response.data);
        formikRef.current?.setFieldValue('scriptIdea', response.data);
      } else {
        toast.error('Something went wrong');
      }
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setGenerating(false);
    }
  };

  const getGenerateButtonLabel = () => {
    if (generating) {
      return <Spinner />;
    }
    if (isSuccess) {
      return (
        <RegenerateButtonLabel>
          Regenerate {selectedTags.length === 0 ? <RetryIcon /> : <RetryOrangeIcon />}
        </RegenerateButtonLabel>
      );
    }
    return 'Generate';
  };

  const filteredTags = query === '' ? allTags : allTags.filter((tag) => {
    return tag?.toLowerCase()?.includes(query.toLowerCase());
  }
  );

  useEffect(() => {
    onChange(name, scriptIdea, selectedTags);
  }, [name, scriptIdea, selectedTags]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .matches(/^[A-Za-z]+$/, 'Only alphabetical letters (a-z) are allowed'),
    scriptIdea: Yup.string()
      .required('Script idea is required')
  });

  const initialValues = {
    name: initialPlayerName || '',
    scriptIdea: initialScenarioIdea || ''
  };

  const formikRef = useRef();

  return (
    <>
      <Wrapper>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            if (loading || generating) {
              return;
            }
            setLoading(true);
            await onContinue(values.name, values.scriptIdea, selectedTags);
            setLoading(false);
            setSubmitting(false);
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <form onSubmit={handleSubmit}>
              <Row>
                <StepTitle>Your name</StepTitle>
                <InputWithDescriptionWrapper>
                  <Input
                    name="name"
                    placeholder='Your name'
                    value={values.name}
                    onChange={(e) => {
                      handleChange(e);
                      setName(e.target.value);
                    }}
                    onBlur={handleBlur}
                    className={errors.name && touched.name ? 'error' : ''}
                  />
                  {errors.name && touched.name && (
                    <ErrorMessage>{errors.name}</ErrorMessage>
                  )}
                </InputWithDescriptionWrapper>
              </Row>

              <Row>
                <ScriptIdeaTitleWrapper>
                  <StepTitle>Script idea</StepTitle>
                  <Description>You can come up with a scenario yourself. In 2-3 sentences, describe the main essence of the scenario.</Description>
                </ScriptIdeaTitleWrapper>
                <div className='flex flex-col gap-y-2'>
                  <TextArea
                    name="scriptIdea"
                    placeholder='Script idea'
                    value={values.scriptIdea}
                    onChange={(e) => {
                      handleChange(e);
                      setScriptIdea(e.target.value);
                    }}
                    onBlur={handleBlur}
                    className={errors.scriptIdea && touched.scriptIdea ? 'error' : ''}
                  />
                  {errors.scriptIdea && touched.scriptIdea && (
                    <ErrorMessage>{errors.scriptIdea}</ErrorMessage>
                  )}
                </div>
              </Row>

              <AdditionalRows>
                <GenerateIdeaSection>
                  <div className='w-full flex flex-row items-center justify-between cursor-pointer' onClick={() => setShowTags(!showTags)}>
                    <Label>Use AI to generate an idea</Label>
                    {showTags ? < MinusIcon width={18} height={18} /> : <PlusIcon width={18} height={18} />}
                  </div>

                  {showTags &&
                    <GenerateIdeaRow className='mt-2'>
                      <Combobox
                        as="div"
                        multiple
                        onClick={(e) => e.preventDefault()}
                        value={selectedTags}
                        onChange={(tags) => setSelectedTags(tags)}
                      >
                        <GenerateInputWrapper>
                          <ComboboxButton className="relative w-full flex items-center rounded-r-md focus:outline-none">
                            <ComboboxInput
                              className="cursor-text relative w-full rounded-xl border-none bg-[rgb(19,19,19)] py-2 pl-4 pr-10 text-left text-white shadow-sm focus:outline-none focus:ring-1 focus:ring-[rgb(255,126,85)] sm:text-sm sm:leading-6 placeholder:text-[rgb(105,105,105)]"
                              onChange={(event) => setQuery(event.target.value)}
                              onBlur={() => setQuery('')}
                              displayValue={() => query}
                              placeholder={'Select tags'}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center gap-2 rounded-r-md px-2 focus:outline-none cursor-default">
                              {query.length !== 0 &&
                                <XMarkIcon className="h-5 w-5 text-gray-200 cursor-pointer" aria-hidden="true"
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    evt.preventDefault();
                                    setQuery('');
                                  }}
                                />
                              }
                              <ChevronUpDownIcon className="h-5 w-5 text-[rgb(105,105,105)] cursor-pointer" aria-hidden="true" />
                            </div>
                          </ComboboxButton>
                          {filteredTags?.length > 0 && (
                            <ComboboxOptions className="absolute z-10 mt-1 max-h-44 w-full overflow-auto rounded-md bg-[rgb(19,19,19)] py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm">
                              {filteredTags?.map((tag) => (
                                <ComboboxOption
                                  key={tag}
                                  value={tag}
                                  className="group relative cursor-default select-none py-2 pl-3 pr-9 text-white data-[focus]:bg-[rgb(255,126,85)] data-[focus]:text-white"
                                >
                                  <span className="pl-5 block truncate font-normal group-data-[selected]:font-semibold first-letter:uppercase">{tag}</span>

                                  <span className="absolute inset-y-0 left-0 hidden items-center pl-1.5 text-[rgb(255,126,85)] group-data-[selected]:flex group-data-[focus]:text-white">
                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                </ComboboxOption>
                              ))}
                            </ComboboxOptions>
                          )}
                        </GenerateInputWrapper>
                      </Combobox>
                      <Description>Tags are only used to generate an idea. Select at least one tag.</Description>
                      {selectedTags.length !== 0 &&
                        <TagWrapper className='flex flex-wrap gap-2'>
                          {selectedTags?.map(tag => {
                            return (
                              <Tag
                                key={`tag_${tag}`}
                                onClick={() => {
                                  setSelectedTags(selectedTags.filter(selectedTag => selectedTag !== tag));
                                }}
                              >
                                <span>
                                  {tag}
                                </span>
                                <XMarkIcon cursor='pointer' width={16} height={16} />
                              </Tag>
                            );
                          })}
                        </TagWrapper>
                      }

                      <Row>
                        <div className='flex gap-2 items-center mt-3 mb-2'>
                          <OutlinedButton
                            disabled={selectedTags.length === 0}
                            onClick={handleGenerateClick}
                            type='button'
                          >
                            {getGenerateButtonLabel()}
                          </OutlinedButton>
                        </div>
                      </Row>
                    </GenerateIdeaRow>
                  }
                </GenerateIdeaSection>

                <ButtonPlaceholder>
                  <OrangeButton isMobile={isMobile} type="submit" disabled={isSubmitting || Object.keys(errors).length > 0}>
                    {loading ? <Spinner size={24} /> : 'Continue'}
                  </OrangeButton>
                </ButtonPlaceholder>
              </AdditionalRows>
            </form>
          )}
        </Formik>
      </Wrapper>
    </>
  );
};

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
`;

const Label = styled.span`
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 400;
  line-height: 18px;
`;

const ScriptIdeaTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InputWithDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Description = styled.span`
  color: rgba(169, 169, 169, 1);
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
`;

const ErrorDescription = styled(Description)`
  color: #FA1E1E;
  font-family: Nunito Sans;
  font-size: 16px;
  line-height: 18px;
`;

const AdditionalRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 16px;
`;

const GenerateIdeaRow = styled(Row)`
  gap: 6px;
`;

const Input = styled.input`
  background: rgba(19, 19, 19, 1);
  border-radius: 15px;
  color: white;
  font-size: 16px;
  line-height: 18px;
  outline: none;
  padding: 16px;
  border: 1px solid transparent;

  :focus {
    border-color: #FF7E55;
    box-shadow: none !important;
  }

  ::placeholder {
    color: rgba(105, 105, 105, 1);
  }

  &.error {
    border: 1px solid #FA1E1E;
  }
`;

const TextArea = styled.textarea`
  background-color: rgba(19, 19, 19, 1) !important;
  border: 1px solid transparent;
  border-radius: 8px;
  color: white;
  min-height: 86px;
  font-size: 16px;
  line-height: 18px;
  outline: none;
  font-family: Nunito Sans;
  padding: 10px;
  width: 100%;
  :focus {
    border-color: #FF7E55;
    box-shadow: none !important;
  }
  ::placeholder {
    color: rgba(105, 105, 105, 1);
  }

  &.error {
    border: 1px solid #FA1E1E;
  }
`;

const OrangeButton = styled.button`
  background-color: rgba(255, 126, 85, 1);
  padding: 8px 24px;
  border-radius: 100px;
  font-family: Nunito Sans;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  box-shadow: 0px 12px 40px 0px rgba(255, 126, 85, 0.12);
  display: flex;
  justify-content: center;
  cursor: pointer;
  width: 100%;
`;

const OutlinedButton = styled.button`
  border: 1px solid rgba(255, 126, 85, 1);
  padding: 8px 12px;
  border-radius: 100px;
  width: 100%;
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgb(255,126,85);
  
  &:hover {
    opacity: 0.8;
  }

  :disabled {
    border: 1px solid #818181;
    color: #818181;
    cursor: not-allowed;
  }
`;

const RegenerateButtonLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  
  & > svg {
  color: #ff7e55;
  stroke: #ff7e55;
}
`;

const GenerateIdeaSection = styled.div`
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #282828;
`;

const GenerateInputWrapper = styled.div`
  position: relative;

  & > button > input {
  height: 50px;
  }
`;

const TagWrapper = styled.div`
  padding-top: 8px;
`;

const Tag = styled.div`
  background: #686868; 
  border-radius: 12px;
  gap: 4px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Nunito Sans;
  font-weight: 400;
  font-size: 16px;
  line-height: 18px;
`;

const ErrorMessage = styled.span`
  color: #FA1E1E;
  font-size: 12px;
  font-family: Nunito Sans;
  margin-bottom: 6px;
`;

const ButtonPlaceholder = styled.div`
  padding-top: 8px;
  width: 100%;
  display: flex;
  gap: 10px;
  
  @media (max-width: 768px) {
    margin-top: auto;
    background: #1A1A1A;
    padding: 10px 24px 16px;
    bottom: 0;
    position: fixed;
    width: 100%;
    z-index: 10;
    left: 0;
    box-sizing: border-box;
  }
`;